/**
 * Makola Market Plate: the stall rolls past, you take what the round asks
 * for and leave the rest.
 *
 * What it exercises is selective attention and inhibition — grabbing the
 * right thing is easy, NOT grabbing the nearest thing is the actual work.
 *
 * On the health content, and this is a boundary rather than a preference:
 * rounds ask for FOOD GROUPS and for the plate model (a starch, a protein, a
 * vegetable). Calling tilapia a protein is food literacy and is taught in
 * every school. Telling a Ghanaian which dishes suit their diabetes or their
 * blood pressure is clinical guidance, so the low-salt and diabetes rounds
 * from the original sketch are deliberately NOT built: that mapping needs a
 * clinician's sign-off, not a developer's guess. Nothing here calls a food
 * good or bad either — a round asks for a group, and everything else is
 * simply not what was asked for.
 */

export type FoodGroup = "starch" | "protein" | "vegetable" | "fruit";

export interface Food {
  key: string;
  /** What Ghanaians call it. The names are the point of this game. */
  label: string;
  group: FoodGroup;
  colour: string;
  /** Rough silhouette, so the belt is readable at a glance and not just a
   *  row of coloured dots. */
  shape: "round" | "oval" | "long" | "heap" | "leaf";
}

export const GROUP_LABELS: Record<FoodGroup, string> = {
  starch: "Starches",
  protein: "Proteins",
  vegetable: "Vegetables",
  fruit: "Fruits",
};

/** Singular, for the plate goal. */
export const GROUP_ONE: Record<FoodGroup, string> = {
  starch: "a starch",
  protein: "a protein",
  vegetable: "a vegetable",
  fruit: "a fruit",
};

export const FOODS: Food[] = [
  // Starches
  { key: "waakye", label: "Waakye", group: "starch", colour: "#8C6239", shape: "heap" },
  { key: "fufu", label: "Fufu", group: "starch", colour: "#EFE3C8", shape: "round" },
  { key: "kenkey", label: "Kenkey", group: "starch", colour: "#D9C79A", shape: "round" },
  { key: "yam", label: "Yam", group: "starch", colour: "#E4D5B7", shape: "long" },
  { key: "plantain", label: "Plantain", group: "starch", colour: "#E0A22B", shape: "long" },
  { key: "rice", label: "Rice", group: "starch", colour: "#F2EFE6", shape: "heap" },

  // Proteins
  { key: "tilapia", label: "Tilapia", group: "protein", colour: "#8FA3B0", shape: "oval" },
  { key: "egg", label: "Egg", group: "protein", colour: "#F7F1E1", shape: "oval" },
  { key: "beans", label: "Beans", group: "protein", colour: "#7A4A2B", shape: "heap" },
  { key: "groundnut", label: "Groundnut", group: "protein", colour: "#C68A4E", shape: "oval" },
  { key: "chicken", label: "Chicken", group: "protein", colour: "#C9925F", shape: "heap" },

  // Vegetables
  { key: "kontomire", label: "Kontomire", group: "vegetable", colour: "#2E7D32", shape: "leaf" },
  { key: "garden-eggs", label: "Garden eggs", group: "vegetable", colour: "#B7D14A", shape: "round" },
  { key: "okro", label: "Okro", group: "vegetable", colour: "#5C9C3B", shape: "long" },
  { key: "tomato", label: "Tomato", group: "vegetable", colour: "#D8412F", shape: "round" },
  { key: "cabbage", label: "Cabbage", group: "vegetable", colour: "#9CC47A", shape: "round" },

  // Fruits
  { key: "pawpaw", label: "Pawpaw", group: "fruit", colour: "#F08A3C", shape: "oval" },
  { key: "mango", label: "Mango", group: "fruit", colour: "#F2B01E", shape: "oval" },
  { key: "pineapple", label: "Pineapple", group: "fruit", colour: "#E8C33A", shape: "round" },
  { key: "orange", label: "Orange", group: "fruit", colour: "#F07B18", shape: "round" },
];

export function foodsInGroup(group: FoodGroup): Food[] {
  return FOODS.filter((food) => food.group === group);
}

/** The stall itself. Procedural, like every other scene in here. */
export const STALL = {
  sky: ["#F6D9A8", "#E8B172"],
  awningStripe: "#C8102E",
  awningCloth: "#F5EFE2",
  table: "#6B4A2C",
  tableEdge: "#4A3220",
  basket: "#A9793F",
  ground: "#4A3524",
};
